// Pure presentational helpers for the public Team Visualization overview cards
// (PRODOPSAI-529). Kept outside the component module so they stay unit-testable
// and so the component body stays flat (SonarCloud S3776 / S7721).

import type {
  ITeamMembershipTreePerson,
  ITeamMembershipTreeRoleGroup,
  ITeamMembershipTreeTeam,
} from '@skystone/shared';
import { roleAbbreviation } from './team-identity';

/**
 * Initials from a display name: first letter of the first two whitespace-
 * separated tokens, uppercased. Mirrors the People directory avatar
 * (PublicPeopleTable). Returns '?' for a blank name.
 */
export function initials(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/).slice(0, 2);
  // Array.from splits by Unicode code point, so a surrogate pair (emoji, some
  // CJK extensions) yields one whole character rather than a broken half.
  return parts.map((p) => (Array.from(p)[0] ?? '').toUpperCase()).join('');
}

export interface TeamMember {
  person: ITeamMembershipTreePerson;
  /** Team role displayName(s) this person holds, in role-group order. */
  roleLabels: string[];
}

/**
 * Distinct members across all of a team's role groups, preserving role-group
 * order then within-group order. A person holding two roles on the same team
 * appears once with both role labels (matches the backend's DISTINCT
 * memberCount). The role labels back the overview hover tooltip (PRODOPSAI-536).
 */
export function distinctTeamMembers(team: ITeamMembershipTreeTeam): TeamMember[] {
  const byId = new Map<string, TeamMember>();
  const members: TeamMember[] = [];
  for (const group of team.roleGroups) {
    for (const person of group.persons) {
      const existing = byId.get(person.personId);
      if (existing === undefined) {
        const member: TeamMember = { person, roleLabels: [group.displayName] };
        byId.set(person.personId, member);
        members.push(member);
      } else if (!existing.roleLabels.includes(group.displayName)) {
        existing.roleLabels.push(group.displayName);
      }
    }
  }
  return members;
}

/** Distinct persons (without role context). Thin wrapper over distinctTeamMembers. */
export function distinctTeamPersons(team: ITeamMembershipTreeTeam): ITeamMembershipTreePerson[] {
  return distinctTeamMembers(team).map((member) => member.person);
}

/** Hover-tooltip text disambiguating shared initials: "Name — Role(s)". */
export function memberTooltip(member: TeamMember): string {
  if (member.roleLabels.length === 0) return member.person.displayName;
  return `${member.person.displayName} — ${member.roleLabels.join(', ')}`;
}

export interface RoleChip {
  key: string;
  /** Compact role code (EM, ENG, UX …) — PRODOPSAI-534. */
  abbrev: string;
  /** Full role name, kept for the chip's tooltip / a11y. */
  label: string;
  count: number;
}

/**
 * One chip per role group: a compact abbreviation (EM, ENG, UX …) + member
 * count. The key is the catalog role id, or a synthetic `entra:<title>` key
 * for entra-title groups (which have a null roleId).
 */
export function roleChipSummary(roleGroups: ITeamMembershipTreeRoleGroup[]): RoleChip[] {
  return roleGroups.map((group) => ({
    key: group.roleId ?? `entra:${group.displayName.toLowerCase()}`,
    abbrev: roleAbbreviation(group.displayName),
    label: group.displayName,
    count: group.persons.length,
  }));
}

/** "1 member" / "N members". */
export function memberCountLabel(count: number): string {
  return count === 1 ? '1 member' : `${count} members`;
}
